import React, { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Link } from 'react-router-dom';
import NoData from 'components/shared/NoData/Index';
import { getDateFormatter } from 'helpers/dateConverter';
import { INotificationCard } from 'core/interface/notificationCard';
import {
  markAllNotificationsRead,
  markNotificationRead,
  NotificationItem,
} from '../../redux/slices/notification';

import './Index.scss';
import useLocales from 'hooks/useLocales';

const NotificationCard: FC<INotificationCard> = (): JSX.Element => {
  const { t } = useLocales();
  const [showAll, setShowAll] = useState(false);
  const { notifications, unreadCount } = useSelector(
    (state: RootState) => state.notifications
  );

  const toInternalPath = useCallback((actionUrl: string) => {
    if (!actionUrl) return '';
    const dashboardIndex = actionUrl.indexOf('/dashboard');
    if (dashboardIndex >= 0) {
      return actionUrl.substring(dashboardIndex);
    }
    try {
      const url = new URL(actionUrl, window.location.origin);
      return url.pathname + url.search;
    } catch {
      return actionUrl.startsWith('/') ? actionUrl : '';
    }
  }, []);

  const notificationsData = useMemo(() => {
    if (showAll) {
      return notifications;
    }
    return notifications.filter((n) => !n.is_read);
  }, [notifications, showAll]);

  const readNotifications = (id: number) => {
    markNotificationRead(id);
  };

  const readAllNotifications = (): void => {
    markAllNotificationsRead();
  };

  const toggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  const renderMessage = (item: NotificationItem) => {
    const actionUrl =
      typeof item.data?.action_url === 'string' ? item.data.action_url : '';
    const internalPath = toInternalPath(actionUrl);

    if (internalPath) {
      return (
        <Link target="_blank" to={internalPath}>
          <span className="card__right-sender">{item.message}</span>
        </Link>
      );
    }

    if (actionUrl) {
      return (
        <a href={actionUrl} target="_blank" rel="noreferrer">
          <span className="card__right-sender">{item.message}</span>
        </a>
      );
    }

    return <span className="card__right-sender">{item.message}</span>;
  };

  return (
    <div className="card_container">
      <div className="card_container_massages">
        <span>
          {' '}
          {`You have ${unreadCount || 'no'} unread message`}
        </span>
      </div>
      <div className="card scrollable">
        {notificationsData.map((item) => (
          <div
            key={item.id}
            className={`card__right${item.is_read ? ' card__right--read' : ''}`}
            onClick={() => readNotifications(item.id)}
          >
            {renderMessage(item)}
            <span className="card__right-date">
              {getDateFormatter(item.created_at, 'DD-MM-YYYY')}
            </span>
          </div>
        ))}

        {unreadCount === 0 && notificationsData.length === 0 ? (
          <div className="no_data">
            <NoData />
          </div>
        ) : (
          ''
        )}
      </div>
      <div className="card__right_btn">
        <button className="card__right_btn_read" onClick={readAllNotifications}>
          {t('Read Everything')}
        </button>
        <button className="card__right_btn_show" onClick={toggleShowAll}>
          {t('Show')} {showAll ? 'Unread' : 'All'}
        </button>
      </div>
    </div>
  );
};

export default NotificationCard;
